import { useEffect } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      render: (element: HTMLElement, options: { sitekey: string }) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
        render: (element: HTMLElement, options: { sitekey: string }) => number;
        reset: (widgetId?: number) => void;
        getResponse: (widgetId?: number) => string;
      };
    };
  }
}

const ENTERPRISE_SCRIPT_SRC = 'https://www.google.com/recaptcha/enterprise.js';

export function getEnterpriseCaptchaClient() {
  if (window.grecaptcha === undefined) {
    return;
  }
  return window.grecaptcha.enterprise ?? window.grecaptcha;
}

export function cleanupEnterpriseCaptcha(): void {
  document
    .querySelectorAll(`script[src*="recaptcha/enterprise.js"]`)
    .forEach((script) => script.remove());

  document.querySelectorAll('.grecaptcha-badge').forEach((badge) => badge.remove());

  if (window.grecaptcha) {
    Reflect.deleteProperty(window, 'grecaptcha');
  }
}

export function resetEnterpriseCaptcha(): void {
  getEnterpriseCaptchaClient()?.reset?.();
}

export function getEnterpriseCaptchaToken(): string {
  return getEnterpriseCaptchaClient()?.getResponse?.() ?? '';
}

/** Injects/tears down the Enterprise reCAPTCHA script for as long as `enabled` is
 * true — mirrors a single render/unmount cycle of a `.g-recaptcha` widget. Shared
 * by every surface using this pattern (ForgotPassword, EmailErrorDialog) so the
 * script lifecycle and cleanup stay identical everywhere it's used. */
export function useEnterpriseRecaptchaScript(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return () => {
        cleanupEnterpriseCaptcha();
      };
    }

    const script = document.createElement('script');
    script.src = ENTERPRISE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.append(script);

    return () => {
      script.remove();
      cleanupEnterpriseCaptcha();
    };
  }, [enabled]);
}
